import { FunctionComponent, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";
import { InputField } from "@/components/InputField/InputField";
import { useAppSelector } from "@/redux/hooks";
import { useSnackBar } from "@/utils/useSnackbar";
import {
	useGetHubspotLeadFieldsQuery,
	useGetHubspotLeadPipelinesQuery,
	useGetHubspotOwnersQuery,
	useGetLeadConfigQuery,
	useGetOAuthStatusQuery,
	useSaveLeadConfigMutation,
} from "@/pages/Private/redux/hubspot/hubspot.api";
import { hubspotSelector } from "@/pages/Private/redux/hubspot/hubspot.slice";

import { HubspotExistingLeadBehavior, LeadConfig, LeadConfigSchema } from "../schema/hubspot";
import {
	getAllowedOptionValue,
	getHubspotFieldOptions,
	HUBSPOT_LEAD_LABEL_FIELD,
	HUBSPOT_LEAD_TYPE_FIELD,
	sanitizeStr,
} from "./hubspotLeadConfig.utils";
import { isHubspotMissingLeadScopeError } from "./hubspotFieldMapping.utils";

export const HubspotLeadConfigTab: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.hubspot.leadConfig.${key}`);
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	const { leadConfig, leadFields, leadPipelines, owners } = useAppSelector(hubspotSelector);
	const [saveLeadConfig, { isLoading: isSaving }] = useSaveLeadConfigMutation();

	useGetLeadConfigQuery();
	const { error: leadFieldsError } = useGetHubspotLeadFieldsQuery();

	useGetHubspotLeadPipelinesQuery();
	useGetHubspotOwnersQuery();
	const { refetch: refetchOAuthStatus } = useGetOAuthStatusQuery();
	const missingLeadScope = isHubspotMissingLeadScopeError(leadFieldsError);

	// The API flags the connection for reauthorization when it answers this way (PREC-3967);
	// pull the status again so the Connection tab's "Reconnect required" banner shows without a reload.
	useEffect(() => {
		if (missingLeadScope) {
			refetchOAuthStatus();
		}
	}, [missingLeadScope, refetchOAuthStatus]);

	const { handleSubmit, setValue, watch, reset } = useForm<LeadConfig>({
		resolver: zodResolver(LeadConfigSchema),
		defaultValues: {
			defaultPipeline: null,
			defaultLeadStage: null,
			leadNameTemplate: "{firstName} {lastName} - Signalist Lead",
			defaultLeadType: null,
			defaultLeadLabel: null,
			defaultOwner: null,
			existingLeadBehavior: HubspotExistingLeadBehavior.SKIP,
		},
	});
	const values = watch();

	useEffect(() => {
		if (leadConfig) {
			reset({
				defaultPipeline: sanitizeStr(leadConfig.defaultPipeline),
				defaultLeadStage: sanitizeStr(leadConfig.defaultLeadStage),
				leadNameTemplate: leadConfig.leadNameTemplate || "{firstName} {lastName} - Signalist Lead",
				defaultLeadType: sanitizeStr(leadConfig.defaultLeadType),
				defaultLeadLabel: sanitizeStr(leadConfig.defaultLeadLabel),
				defaultOwner: sanitizeStr(leadConfig.defaultOwner),
				existingLeadBehavior: leadConfig.existingLeadBehavior ?? HubspotExistingLeadBehavior.SKIP,
			});
		}
	}, [leadConfig, reset]);

	const pipelineOptions = useMemo(
		() =>
			leadPipelines?.pipelines.map((pipeline) => ({ id: pipeline.id, title: pipeline.label })) ??
			[],
		[leadPipelines]
	);
	const stageOptions = useMemo(() => {
		const pipeline = leadPipelines?.pipelines.find((item) => item.id === values.defaultPipeline);

		return pipeline?.stages.map((stage) => ({ id: stage.id, title: stage.label })) ?? [];
	}, [leadPipelines, values.defaultPipeline]);
	const ownerOptions = useMemo(
		() =>
			owners?.owners.map((owner) => ({
				id: owner.id,
				title: `${owner.firstName || ""} ${owner.lastName || ""} (${owner.email})`.trim(),
			})) ?? [],
		[owners]
	);
	const leadTypeOptions = useMemo(
		() => getHubspotFieldOptions(leadFields?.fields, HUBSPOT_LEAD_TYPE_FIELD),
		[leadFields]
	);
	const leadLabelOptions = useMemo(
		() => getHubspotFieldOptions(leadFields?.fields, HUBSPOT_LEAD_LABEL_FIELD),
		[leadFields]
	);
	const behaviorOptions = Object.values(HubspotExistingLeadBehavior).map((behavior) => ({
		id: behavior,
		title: ts(`existingLeadBehavior.${behavior}`),
	}));

	const save = handleSubmit(async (formValues) => {
		try {
			await saveLeadConfig({
				...formValues,
				defaultPipeline: sanitizeStr(formValues.defaultPipeline),
				defaultLeadStage: sanitizeStr(formValues.defaultLeadStage),
				defaultOwner: sanitizeStr(formValues.defaultOwner),
				defaultLeadType: getAllowedOptionValue(formValues.defaultLeadType, leadTypeOptions),
				defaultLeadLabel: getAllowedOptionValue(formValues.defaultLeadLabel, leadLabelOptions),
			}).unwrap();
			handleOpenSnackbar(ts("saved"));
		} catch {
			handleOpenSnackbar(ts("saveError"));
		}
	});

	return (
		<form className="flex w-full flex-col gap-4" onSubmit={save}>
			{renderMessageSnackbar()}
			{missingLeadScope && (
				<div
					className="rounded-lg border border-status-warning bg-status-warning-light p-3 text-sm text-text"
					data-testid="hubspot-lead-scope-hint"
				>
					{ts("missingScopeHint")}
				</div>
			)}
			<div className="text-sm text-text-meta">{ts("description")}</div>
			<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
				<SelectField
					label={ts("defaultPipeline")}
					options={pipelineOptions}
					value={values.defaultPipeline}
					onSelect={(value) => {
						setValue("defaultPipeline", value);
						setValue("defaultLeadStage", null);
					}}
				/>
				<SelectField
					label={ts("defaultStage")}
					options={stageOptions}
					value={values.defaultLeadStage}
					onSelect={(value) => setValue("defaultLeadStage", value)}
				/>
				<InputField
					handleChange={(event) => setValue("leadNameTemplate", event.target.value)}
					information={ts("leadNameTemplateHelp")}
					label={ts("leadNameTemplate")}
					name="leadNameTemplate"
					value={values.leadNameTemplate || ""}
				/>
				<SelectField
					label={ts("defaultOwner")}
					options={ownerOptions}
					value={values.defaultOwner}
					onSelect={(value) => setValue("defaultOwner", value)}
				/>
				<SelectField
					disabled={!leadTypeOptions.length}
					information={!leadTypeOptions.length ? ts("noLeadTypeOptions") : undefined}
					label={ts("defaultLeadType")}
					options={leadTypeOptions}
					value={values.defaultLeadType}
					onSelect={(value) => setValue("defaultLeadType", value)}
				/>
				<SelectField
					disabled={!leadLabelOptions.length}
					information={!leadLabelOptions.length ? ts("noLeadLabelOptions") : undefined}
					label={ts("defaultLeadLabel")}
					options={leadLabelOptions}
					value={values.defaultLeadLabel}
					onSelect={(value) => setValue("defaultLeadLabel", value)}
				/>
				<SelectField
					label={ts("existingLeadBehaviorLabel")}
					options={behaviorOptions}
					value={values.existingLeadBehavior}
					onSelect={(value) =>
						setValue("existingLeadBehavior", value as HubspotExistingLeadBehavior)
					}
				/>
			</div>
			<div className="flex justify-end">
				<Button
					color={ButtonColor.ACTION}
					isLoading={isSaving}
					size={ButtonSize.S}
					title={t("basics.save")}
					type="submit"
				/>
			</div>
		</form>
	);
};

const SelectField = ({
	label,
	options,
	value,
	onSelect,
	disabled,
	information,
}: {
	label: string;
	options: AutoCompleteItem[];
	value?: string | null;
	onSelect: (value: string) => void;
	disabled?: boolean;
	information?: string;
}) => (
	<Dropdown
		key={`${label}-${value ?? "empty"}-${options.length}`}
		data={options}
		defaultValue={value ? options.find((option) => String(option.id) === String(value)) : undefined}
		disabled={disabled}
		floating={true}
		handleSelect={(item: AutoCompleteItem) => onSelect(String(item.id))}
		information={information}
		label={label}
	/>
);
